import { Breadcrumb, Button, Checkbox, Col, Container, Input, Item, List, Loading, Row } from "react-dsgov";
import CardComponente from "./CardComponente/CardComponente";
import { useCallback, useEffect, useState } from "react";
import IComponente, { ICategoria, ITag, ITecnologia } from "../../interfaces/IComponente";
import { useNavigate } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import useCatalogoService from "../../hooks/useCatalogoService";
import useLoadingState from "../../state/hooks/useLoadingState";
import useSubtituloState from "../../state/hooks/useSubtituloState";
import ITema from "../../interfaces/ITema";
import { useClear } from "../../hooks/useClear";
import classNames from "classnames";
import useMessageScroll from "../../hooks/useMessageScroll";
import { buildPageTitle } from "../../config/portalMode";
import PageMetadata from "../../components/PageMetadata/PageMetadata";

export interface ICatalogoComponentesProps {
    somenteApis?: boolean;
}

const CatalogoComponentes: React.FC<ICatalogoComponentesProps> = ({ somenteApis = false }) => {
    const navigate = useNavigate();
    const catalogoService = useCatalogoService();
    const clear = useClear();

    const [, setSubtitulo] = useSubtituloState();
    const [, setLoading] = useLoadingState();

    const [componentes, setComponentes] = useState<IComponente[]>([]);

    // Armazena a lista completa de filtros disponíveis vindos da API.
    const [todasTecnologias, setTodasTecnologias] = useState<ITecnologia[]>([]);
    const [todasCategorias, setTodasCategorias] = useState<ICategoria[]>([]);
    const [todasTags, setTodasTags] = useState<ITag[]>([]);
    const [todosTemas, setTodosTemas] = useState<ITema[]>([]);

    // Armazena apenas os IDs dos filtros que o usuário selecionou.
    const [idsTecnologiasSelecionadas, setIdsTecnologiasSelecionadas] = useState<number[]>([]);
    const [idsCategoriasSelecionadas, setIdsCategoriasSelecionadas] = useState<number[]>([]);
    const [idsTagsSelecionadas, setIdsTagsSelecionadas] = useState<number[]>([]);
    const [idsTemasSelecionados, setIdsTemasSelecionados] = useState<number[]>([]);
    const [termoBusca, setTermoBusca] = useState<string>("");

    // ID fixo da categoria "API Rest", utilizado quando a página é o catálogo de APIs.
    const [idCategoriaApiRest, setIdCategoriaApiRest] = useState<number | undefined>(undefined);

    const [pagina, setPagina] = useState<number>(1);
    const [carregouTodos, setCarregouTodos] = useState<boolean>(false);

    // Estado de carregamento para a busca inicial (quando os filtros mudam).
    const [carregando, setCarregando] = useState<boolean>(false);
    // Estado de carregamento para a paginação (infinite scroll).
    const [carregandoMais, setCarregandoMais] = useState<boolean>(false);

    const messageScroll = useMessageScroll();


    useEffect(() => {
        setSubtitulo(somenteApis ? "Catálogo de APIs" : "Catálogo de Componentes");
    }, [setSubtitulo, somenteApis]);
    
    // Função genérica para adicionar ou remover um ID do array de filtros selecionados.
    const handleFilterChange = (id: number, checked: boolean, setIds: React.Dispatch<React.SetStateAction<number[]>>) => {
        messageScroll();
        
        setIds(prevIds => {
            if (checked) {
                return [...prevIds, id];
            } else {
                return prevIds.filter(prevId => prevId !== id);
            }
        });
    };

    // Efeito para carregar os filtros disponíveis (tecnologias, categorias, etc.) na montagem do componente.
    useEffect(() => {
        catalogoService.getTecnologias().then(setTodasTecnologias);
        catalogoService.getCategorias().then(categorias => {
            setTodasCategorias(categorias);

            if (somenteApis) {
                const categoriaApiRest = categorias.find(c =>
                    c.nome.trim().toLowerCase().replace(/[^a-z0-9]/g, '') === "apirest"
                );
                setIdCategoriaApiRest(categoriaApiRest?.id);
            }
        });
        catalogoService.getTags().then(setTodasTags);
        catalogoService.getTemas().then(setTodosTemas);
    }, [somenteApis, catalogoService]);

    // Efeito para buscar os componentes sempre que um filtro for alterado.
    useEffect(() => {
        // No catálogo de APIs, aguarda o carregamento das categorias para descobrir o ID da categoria "API Rest".
        if (somenteApis && idCategoriaApiRest === undefined) {
            setCarregando(true);
            return;
        }

        const idsCategorias = somenteApis && idCategoriaApiRest !== undefined
            ? [...idsCategoriasSelecionadas, idCategoriaApiRest]
            : idsCategoriasSelecionadas;

        const timeoutBusca = window.setTimeout(() => {
            setCarregando(true);
            setLoading(true);

            catalogoService.getComponentes(idsCategorias, idsTecnologiasSelecionadas, idsTagsSelecionadas, idsTemasSelecionados, termoBusca)
                .then(componentes => {
                    setComponentes(componentes);
                    setPagina(1);
                    setCarregouTodos(false);
                })
                .finally(() => {
                    setCarregando(false);
                    setLoading(false);
                });
        }, 400);

        return () => window.clearTimeout(timeoutBusca);
    }, [idsTecnologiasSelecionadas, idsCategoriasSelecionadas, idsTagsSelecionadas, idsTemasSelecionados, termoBusca, idCategoriaApiRest, catalogoService, setLoading, somenteApis]);


    // Função para carregar mais componentes (próxima página) para o infinite scroll.
    const carregarMais = useCallback(() => {
        if (carregandoMais || carregouTodos) return;

        setCarregandoMais(true);
        const proximaPagina = pagina + 1;

        const idsCategorias = somenteApis && idCategoriaApiRest !== undefined
            ? [...idsCategoriasSelecionadas, idCategoriaApiRest]
            : idsCategoriasSelecionadas;

        catalogoService.getComponentes(idsCategorias, idsTecnologiasSelecionadas, idsTagsSelecionadas, idsTemasSelecionados, termoBusca, proximaPagina)
            .then(novosComponentes => {
                if (novosComponentes.length === 0) {
                    setCarregouTodos(true);
                } else {
                    setComponentes(oldComponentes => [...oldComponentes, ...novosComponentes]);
                    setPagina(proximaPagina);
                }
            })
            .finally(() => {
                setCarregandoMais(false);
            });
    }, [carregandoMais, carregouTodos, pagina, idsCategoriasSelecionadas, idsTecnologiasSelecionadas, idsTagsSelecionadas, idsTemasSelecionados, termoBusca, idCategoriaApiRest, catalogoService, somenteApis]);


    const tituloPagina = somenteApis ? "Catálogo de APIs" : "Catálogo de Componentes";
    const labelBreadcrumb = somenteApis ? "Catálogo de APIs" : "Catalogo de Componentes";

    // No catálogo de APIs, oculta a categoria "API Rest" da lista de filtros,
    // pois ela já está aplicada automaticamente na chamada do endpoint.
    const categoriasExibidas = somenteApis
        ? todasCategorias.filter(c => c.id !== idCategoriaApiRest)
        : todasCategorias;

    return (
        <>
            <PageMetadata title={buildPageTitle(tituloPagina)} />
            <Container fluid={clear} className={classNames({ "sem-margin": clear })}>
                {!clear && (
                    <Breadcrumb>
                        <Breadcrumb.Item home href="#" onClick={() => navigate("/")}></Breadcrumb.Item>
                        <Breadcrumb.Item>{labelBreadcrumb}</Breadcrumb.Item>
                    </Breadcrumb>
                )}
                <Row>
                    <Col md={3}>
                        <List title="Filtros">
                            <Item showDividerAfter>
                                <Input
                                    id="busca-componentes"
                                    type="search"
                                    label="Buscar nos componentes"
                                    value={termoBusca}
                                    onChange={(event) => setTermoBusca((event.target as HTMLInputElement).value)}
                                    placeholder="Digite um termo"
                                />
                            </Item>
                            <Item showDividerAfter collapsable initiallyExpanded subItems={
                                <>
                                    {todasTecnologias?.map((tecnologia) =>
                                        <Item key={tecnologia.id}>
                                            <Checkbox
                                                id={`checkbox-tecnologia-${tecnologia.id}`}
                                                label={tecnologia.nome}
                                                checked={idsTecnologiasSelecionadas.includes(tecnologia.id)}
                                                onChange={(event) => handleFilterChange(tecnologia.id, (event.target as HTMLInputElement).checked, setIdsTecnologiasSelecionadas)}
                                            />
                                        </Item>
                                    )}
                                </>
                            }>
                                Tecnologias
                            </Item>
                            <Item showDividerAfter collapsable initiallyExpanded subItems={
                                <>
                                    {categoriasExibidas?.map((categoria) =>
                                        <Item key={categoria.id}>
                                            <Checkbox
                                                id={`checkbox-categoria-${categoria.id}`}
                                                label={categoria.nome}
                                                checked={idsCategoriasSelecionadas.includes(categoria.id)}
                                                onChange={(event) => handleFilterChange(categoria.id, (event.target as HTMLInputElement).checked, setIdsCategoriasSelecionadas)}
                                            />
                                        </Item>
                                    )}
                                </>
                            }>
                                Categorias
                            </Item>
                            <Item showDividerAfter collapsable initiallyExpanded subItems={
                                <>
                                    {todosTemas?.map((tema) =>
                                        <Item key={tema.id}>
                                            <Checkbox
                                                id={`checkbox-tema-${tema.id}`}
                                                label={tema.nome}
                                                checked={idsTemasSelecionados.includes(tema.id)}
                                                onChange={(event) => handleFilterChange(tema.id, (event.target as HTMLInputElement).checked, setIdsTemasSelecionados)}
                                            />
                                        </Item>
                                    )}
                                </>
                            }>
                                Temas de Interesse
                            </Item>
                            <Item showDividerAfter collapsable initiallyExpanded subItems={
                                <>
                                    {todasTags?.map((tag) =>
                                        <Item key={tag.id}>
                                            <Checkbox
                                                id={`checkbox-tag-${tag.id}`}
                                                label={`#${tag.nome}`}
                                                checked={idsTagsSelecionadas.includes(tag.id)}
                                                onChange={(event) => handleFilterChange(tag.id, (event.target as HTMLInputElement).checked, setIdsTagsSelecionadas)}
                                            />
                                        </Item>
                                    )}
                                </>
                            }>
                                Tags
                            </Item>
                        </List>
                    </Col>
                    <Col>
                        {/* Exibe o spinner de carregamento centralizado apenas na busca inicial. */}
                        {carregando && <Loading />}
                        
                        {/* Exibe a mensagem de "sem resultados" se a busca inicial terminar e não houver componentes. */}
                        {!carregando && componentes.length === 0 && (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                                <p className="h5 text-muted">Sem resultados para os filtros selecionados.</p>
                            </div>
                        )}

                        {/* O InfiniteScroll só é renderizado se houver componentes para exibir (após a busca inicial). */}
                        {!carregando && componentes.length > 0 && (
                            <InfiniteScroll
                                dataLength={componentes.length}
                                next={carregarMais}
                                hasMore={!carregouTodos}
                                loader={
                                <>
                                    {!carregando && !carregandoMais && <div className="d-flex justify-content-center"><Button primary onClick={carregarMais}>Carregar mais</Button></div>}
                                    {carregandoMais && <Loading />}
                                </>}
                                endMessage={<p style={{ textAlign: 'center', marginTop: '20px' }}><b>Fim dos resultados.</b></p>}
                            >
                                <div className="mb-5 d-flex mt-1 align-content-start justify-content-center flex-wrap">
                                    {componentes.map((componente) =>
                                        <>{componente.autorizado && <CardComponente key={componente.id} componente={componente} origemApi={somenteApis} />}</>
                                    )}
                                </div>
                            </InfiniteScroll>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default CatalogoComponentes;
